import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { CheckCircle2, Globe2, Info, Map, Users } from 'lucide-react';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Skeleton } from 'components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { DataTable } from 'components/DataTable';
import { useToast } from 'components/ui/toast';
import AppContext from 'contexts/AppContext';
import { hasUserPermission, PERMISSIONS } from 'utils/permissionUtils';
import { apiErrorMessage } from 'utils/errorMessage';
import { subdivisionLabel } from 'utils/subdivisionLabel';
import { useCountries, useSubdivisions, useUpdateSubdivision } from 'hooks/useReferentials';
import type { Subdivision } from 'api/referentialsApi';
import ReferentialStats from '../components/ReferentialStats';
import ReferentialPageHeader from '../components/ReferentialPageHeader';
import RowActions from '../components/RowActions';
import StatusCell from '../components/StatusCell';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import { deactivateCopy } from '../confirmCopy';
import SubdivisionFormDialog from './SubdivisionFormDialog';

const ALL = '__all__';

const AdminReferentialGeography = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { currentUser } = useContext(AppContext);
  const canManage = hasUserPermission(currentUser, PERMISSIONS.DASHBOARD_REFERENTIALS_MANAGE);

  const { data: countryData, error: countryError } = useCountries();
  const { data, isLoading, error } = useSubdivisions();
  const updateSubdivision = useUpdateSubdivision();
  const subdivisions = useMemo(() => data ?? [], [data]);
  const countries = useMemo(() => countryData ?? [], [countryData]);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deactivating, setDeactivating] = useState<Subdivision | null>(null);
  const [countryFilter, setCountryFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);

  // By id rather than by value, so the dialog keeps showing live data after a write.
  const editing = subdivisions.find((subdivision) => subdivision.id === editingId) ?? null;

  // Countries the ops has not signed off on. The design asks for this banner explicitly: the
  // Spanish and British referentials are illustrative values, not an arbitrated list.
  const pendingCountries = countries.filter((country) => country.is_active && !country.is_validated);

  const rows = useMemo(
    () =>
      subdivisions.filter(
        (subdivision) =>
          (countryFilter === ALL || subdivision.country_code === countryFilter) &&
          (statusFilter === ALL || String(subdivision.is_active) === statusFilter)
      ),
    [subdivisions, countryFilter, statusFilter]
  );

  // One subdivision type on screen means we can name the column precisely; several means we
  // cannot, and a generic header beats calling a Spanish province a department.
  const columnLabel = useMemo(() => {
    // No Set spread: the frontend compiles to ES5, where iterating one needs downlevelIteration.
    const types = rows.map((subdivision) => subdivision.country_subdivision_type);
    const distinct = types.filter((type, index) => types.indexOf(type) === index);
    return distinct.length === 1 ? subdivisionLabel(t, distinct[0]) : subdivisionLabel(t, '');
  }, [rows, t]);

  const stats = useMemo(
    () => [
      {
        key: 'total',
        label: subdivisionLabel(t, 'department', 2),
        value: subdivisions.length,
        icon: Map,
        hint: t('Every entry in the referential, active or not'),
      },
      {
        key: 'active',
        label: t('Active'),
        value: subdivisions.filter((s) => s.is_active).length,
        icon: CheckCircle2,
        hint: t('Offered when scoping a technician'),
        tooltip: t(
          'A deactivated entry stays on the technicians that already carry it; it simply leaves the pickers.'
        ),
      },
      {
        key: 'countries',
        label: t('Countries'),
        value: new Set(subdivisions.map((s) => s.country_code)).size,
        icon: Globe2,
        hint: t('Countries with at least one entry'),
      },
      {
        key: 'technicians',
        label: t('Technicians'),
        value: subdivisions.reduce((total, s) => total + s.technician_count, 0),
        icon: Users,
        hint: t('Scoping links, all entries combined'),
        tooltip: t('Counts the active technicians scoped here. A deactivated one does not show up, and neither does an area nobody covers.'),
      },
    ],
    [subdivisions, t]
  );

  // Memoised so the columns can depend on it without rebuilding on every render.
  const toggleActive = useCallback(
    (subdivision: Subdivision) =>
      updateSubdivision.mutate(
        { id: subdivision.id, data: { isActive: !subdivision.is_active } },
        {
          onSuccess: () => {
            setDeactivating(null);
            toast.success(
              subdivision.is_active
                ? t('{{name}} deactivated', { name: subdivision.name })
                : t('{{name}} reactivated', { name: subdivision.name })
            );
          },
          onError: (mutationError) => toast.error(apiErrorMessage(t, mutationError)),
        }
      ),
    [updateSubdivision, toast, t]
  );

  const columns = useMemo<ColumnDef<Subdivision>[]>(() => {
    const base: ColumnDef<Subdivision>[] = [
      { accessorKey: 'country_name', header: t('Country') },
      {
        accessorKey: 'name',
        header: columnLabel,
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        accessorKey: 'code',
        header: t('Code'),
        cell: ({ row }) => (
          <Badge variant="outline" className="font-mono text-xs font-normal tracking-tight">
            {row.original.code}
          </Badge>
        ),
      },
      {
        accessorKey: 'technician_count',
        header: t('Technicians'),
        cell: ({ row }) => (
          <span className="font-mono text-sm tabular-nums">{row.original.technician_count}</span>
        ),
      },
      {
        accessorKey: 'is_active',
        header: t('Status'),
        cell: ({ row }) => <StatusCell active={row.original.is_active} />,
      },
    ];

    // A read-only account gets the table without its actions, rather than buttons that 403.
    if (!canManage) return base;

    return [
      ...base,
      {
        id: 'actions',
        header: t('Actions'),
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onEdit={() => {
              setEditingId(row.original.id);
              setFormOpen(true);
            }}
            isActive={row.original.is_active}
            onDeactivate={() => setDeactivating(row.original)}
            onActivate={() => toggleActive(row.original)}
          />
        ),
      },
    ];
  }, [t, canManage, columnLabel, toggleActive]);

  const openCreate = () => {
    setEditingId(null);
    setFormOpen(true);
  };

  return (
    <div className="container mx-auto space-y-4 p-6">
      <ReferentialPageHeader
        icon={Map}
        title={t('Sectorisation')}
        subtitle={t('Countries and areas a technician can be scoped to')}
        actionLabel={canManage ? t('New entry') : undefined}
        onAction={canManage ? openCreate : undefined}
      />

      <ReferentialStats stats={stats} loading={isLoading} />

      {pendingCountries.length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            {t('Awaiting validation: {{countries}}. Only the French referential is confirmed.', {
              countries: pendingCountries.map((country) => country.name).join(', '),
            })}
          </span>
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((row) => (
                <Skeleton key={row} className="h-10 w-full" />
              ))}
            </div>
          ) : error || countryError ? (
            <p className="text-sm text-red-600">{apiErrorMessage(t, error ?? countryError)}</p>
          ) : (
            <DataTable
              data={rows}
              columns={columns}
              // A referential is administered, not exported — the design has no CSV here.
              showExport={false}
              // A withdrawn entry stays readable but recedes; it is not a live choice any more.
              rowClassName={(row: Subdivision) => (row.is_active ? undefined : 'opacity-60')}
              customFilters={() => (
                <>
                  <Select value={countryFilter} onValueChange={setCountryFilter}>
                    <SelectTrigger className="w-[180px]" aria-label={t('Country')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>{t('All countries')}</SelectItem>
                      {countries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[160px]" aria-label={t('Status')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>{t('All statuses')}</SelectItem>
                      <SelectItem value="true">{t('Active')}</SelectItem>
                      <SelectItem value="false">{t('Inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
              // Distinguishes an empty referential from a search or a filter that matched nothing.
              emptyMessage={subdivisions.length === 0 ? t('No entry yet') : t('No result for this search')}
              searchPlaceholder={t('A department, a code, a country…')}
              pageSize={25}
            />
          )}
          <p className="mt-4 text-xs text-muted-foreground">
            {t('{{active}} active · {{inactive}} inactive. A deactivated entry stays visible on the technicians already scoped to it and only leaves the pickers.', {
              active: subdivisions.filter((s) => s.is_active).length,
              inactive: subdivisions.filter((s) => !s.is_active).length,
            })}
          </p>
        </CardContent>
      </Card>

      <SubdivisionFormDialog
        open={formOpen}
        subdivision={editing}
        countries={countries}
        onClose={() => setFormOpen(false)}
      />

      {deactivating && (
        <ConfirmDeleteDialog
          open
          tone="warning"
          {...deactivateCopy(t, deactivating.name)}
          loading={updateSubdivision.isPending}
          onClose={() => setDeactivating(null)}
          onConfirm={() => toggleActive(deactivating)}
        />
      )}
    </div>
  );
};

export default AdminReferentialGeography;
